import React from "react";
import { View, Text, Pressable } from "react-native";
import { Feather } from "@expo/vector-icons";
import { useTheme } from "@react-navigation/native";

interface ItemCardProps {
  children: React.ReactNode;
  onClick?: () => void;
  badge?: React.ReactNode;
  variant?: "default";
}

const ItemCardContext = React.createContext<{
  badge?: React.ReactNode;
} | null>(null);

const ItemCard = (props: ItemCardProps) => {
  const { colors } = useTheme();

  return (
    <ItemCardContext.Provider value={{ badge: props.badge }}>
      <Pressable
        onPress={props.onClick}
        className="bg-white rounded-lg p-3 mb-2 border border-gray-100 relative overflow-hidden flex-row items-center justify-between shadow-sm"
      >
        <View className="flex-row items-center justify-between flex-1">
          <View className="flex-1 p-1">
            {React.Children.map(props.children, (child) => {
              if (
                React.isValidElement(child) &&
                (child.type === ItemCard.Header || child.type === ItemCard.Content || child.type === ItemCard.Footer)
              ) {
                return React.cloneElement(child);
              }
              return null;
            })}
          </View>
          <Feather name="chevron-right" size={20} color={colors.text} className="ml-2" />
        </View>
      </Pressable>
    </ItemCardContext.Provider>
  );
};

export const ItemCardHeader = ({ children }: { children: React.ReactNode }) => {
  const context = React.useContext(ItemCardContext);
  return (
    <View className="flex-row items-center justify-between mb-2">
      <Text>{children}</Text>
      {context?.badge && <View className="ml-2 rounded-xl px-2 py-1">{context.badge}</View>}
    </View>
  );
};

export const ItemCardContent = ({ children }: { children: React.ReactNode }) => (
  <View className="flex-row items-center justify-between flex-1">{children}</View>
);

const Footer = ({ children }: { children: React.ReactNode }) => {
  return (
    <View className="flex-row items-center justify-between mt-3">
      <Text className="text-base font-bold text-gray-900">{children}</Text>
    </View>
  );
};

ItemCard.Header = ItemCardHeader;
ItemCard.Content = ItemCardContent;
ItemCard.Footer = Footer;

export default ItemCard;
